import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FadeIn from "@/components/FadeIn";
import Lightbox from "@/components/Lightbox";
import SkylineDivider from "@/components/SkylineDivider";
import { IMAGES } from "@/lib/images";

// Each amenity carries a gallery of photos. The card image rotates through
// them automatically every 5 seconds. As you upload more shots later, just
// append them to the corresponding gallery array — no other code changes needed.
const AMENITIES = [
  {
    title: "The Barn",
    desc: "The gathering heart of the property. Full kitchen, a giant covered porch overlooking the pond, and sizable indoor space for meetings, small parties, workshops, and group dinners.",
    gallery: [IMAGES.oct26BarnKitchenBar, IMAGES.oct26BarnPorchFlag, IMAGES.oct26BarnPorchCouches, IMAGES.imgBarnPondCattails, IMAGES.imgBarnEchinacea, IMAGES.imgBarnAcrossPond, IMAGES.imgBarnDeckChairs, IMAGES.imgBarnPondSummer, IMAGES.imgOverviewSnowyBarn, IMAGES.imgBarnWinterSide, IMAGES.imgBarnKitchenHD, IMAGES.imgBarnKitchen, IMAGES.newBarn01],
  },
  {
    title: "Barn Bath House",
    desc: "Two full private bathrooms tucked below the barn — each with a rain-head shower, toilet, and rustic vessel sink. Available to guests staying in Creekside, Cattail, and the Granary (cottages without an in-room bath). Reserved for guest use only, so you'll never share with anyone but the person you arrived with.",
    gallery: [IMAGES.imgBathhouse1, IMAGES.imgBathhouse2, IMAGES.imgBathhouseSink],
  },
  {
    title: "The Barn Office",
    desc: "The reimagined upstairs hayloft — a quiet workspace with oversized leather couches, an antique desk, a gas fireplace, and coffee. Perfect for a slow morning or a thoughtful workday.",
    gallery: [IMAGES.imgBarnOfficeSnowyDeck, IMAGES.barnSuite],
  },
  {
    title: "Corn Crib Sauna",
    desc: "An infrared sauna tucked inside the original corn crib — the old slatted wood walls preserved as part of the experience. A quiet way to thaw out, slow down, or close out a long day.",
    gallery: [IMAGES.imgCornCribSaunaHD, IMAGES.imgCornCribSauna2, IMAGES.imgSauna1],
  },
  {
    title: "Movement and Meditation Studio",
    desc: "A dedicated indoor space with mats, props, and natural light — used for yoga, breathwork, stretching, journaling, or simply lying still for a while. Open to guests for personal practice or small group classes.",
    gallery: [IMAGES.imgWellnessYoga, IMAGES.imgWellnessStudioLounge],
  },
  {
    title: "Gym",
    desc: "Free weights, cardio equipment, and the kind of quiet that makes a morning workout feel sacred. Available to anyone staying with us.",
    gallery: [IMAGES.imgGymUser, IMAGES.imgGymLogo, IMAGES.mlsGym],
  },
  {
    title: "Reading & Reflection Cottage",
    desc: "A small, quiet cottage with two comfortable chairs and a wall of books. Made for slow afternoons, prayer, meditation, and the rare luxury of an uninterrupted thought.",
    gallery: [IMAGES.imgReflectionCottage6, IMAGES.imgReflectionCottage7],
  },
  {
    title: "The Grounds",
    desc: "Fifty acres of walking paths, wildflower meadows, vegetable beds bursting with the season, a central fire pit stocked with wood, and every quiet corner in between. Guests are welcome to wander, sketch, photograph, or simply be still.",
    gallery: [
      IMAGES.oct26HilltopSunset,
      IMAGES.oct26HilltopFirepit,
      IMAGES.oct26FarmSkyline,
      IMAGES.oct26GardenColeus,
      IMAGES.oct26SwallowtailZinnia,
      IMAGES.oct26Anemones,
      IMAGES.oct26FarmHillside,
      IMAGES.oct26FarmDeer,
      IMAGES.oct26BunnySkyline,
      IMAGES.oct26FarmFlag,
      IMAGES.oct26BeeOnZinnia,
      IMAGES.oct26FarmLeaves,
      IMAGES.imgVegGardenHenDenHD,
      IMAGES.imgTrailNature,
      IMAGES.imgHeronWillow,
      IMAGES.imgChickens,
      IMAGES.imgVegGardenPergolaHD,
      IMAGES.imgVegGardenEdit,
      IMAGES.pathInField,
      IMAGES.imgVegRaisedBeds,
      IMAGES.treeInField,
      IMAGES.imgFlowers3,
    ],
  },
];

function RotatingImage({ gallery, alt, onClick, shapeIndex = 0 }) {
  const [idx, setIdx] = useState(0);
  const safeGallery = gallery && gallery.length > 0 ? gallery : null;

  // Nature-y organic shapes — cathedral arches, leaf petals, garden gates
  const shapes = [
    "rounded-t-[240px] rounded-b-lg",                                  // cathedral/arched-top like a cottage door
    "rounded-[65%_35%_60%_40%_/_50%_45%_55%_50%]",                     // organic leaf-petal
    "rounded-tl-[140px] rounded-br-[140px] rounded-tr-[30px] rounded-bl-[30px]", // asymmetric leaf
    "rounded-l-[160px] rounded-r-lg",                                  // full left arch — barn-door feel
    "rounded-[40%_60%_50%_50%_/_60%_40%_50%_50%]",                     // soft squircle, organic
    "rounded-t-[180px] rounded-b-[30px]",                              // domed arbor
    "rounded-tr-[160px] rounded-bl-[160px] rounded-tl-[20px] rounded-br-[20px]", // pond-ripple asymmetric
  ];
  const shapeClass = shapes[shapeIndex % shapes.length];

  useEffect(() => {
    if (!safeGallery || safeGallery.length < 2) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % safeGallery.length);
    }, 6500);
    return () => clearInterval(id);
  }, [safeGallery]);

  // Empty gallery — show a placeholder "Image coming soon" tile
  if (!safeGallery) {
    return (
      <div className={`relative aspect-[4/3] overflow-hidden bg-oat flex items-center justify-center border border-moss/15 ${shapeClass}`}>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full border-2 border-moss/40 flex items-center justify-center">
            <span className="text-moss/50 text-xl">✦</span>
          </div>
          <p className="text-moss/60 font-serif italic text-lg">Photo coming soon</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative aspect-[4/3] overflow-hidden bg-oat cursor-zoom-in group/img shadow-sm ${shapeClass}`}
      onClick={() => onClick?.(idx)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.(idx)}
    >
      {safeGallery.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out group-hover/img:scale-[1.03]"
          style={{
            opacity: i === idx ? 1 : 0,
            transitionDuration: "3500ms",
            transitionProperty: "opacity, transform",
          }}
        />
      ))}
      {safeGallery.length > 1 && (
        <div className="absolute bottom-4 right-5 flex gap-1.5">
          {safeGallery.map((_, i) => (
            <span
              key={i}
              className={`block h-1.5 rounded-full transition-all duration-500 ${
                i === idx ? "w-5 bg-cream" : "w-1.5 bg-cream/50"
              }`}
            />
          ))}
        </div>
      )}
      <div className="absolute top-4 right-5 bg-ink/70 text-cream text-[10px] uppercase tracking-widest px-2.5 py-1 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 rounded-full">
        Click to expand
      </div>
    </div>
  );
}

export default function Amenities() {
  const [lb, setLb] = useState({ open: false, images: [], index: 0, title: "" });
  const openLightbox = (amenity, startIdx = 0) => {
    setLb({ open: true, images: amenity.gallery, index: startIdx, title: amenity.title });
  };
  return (
    <div data-testid="amenities-page">
      <section className="container-x pt-12 lg:pt-24 pb-16" data-testid="amenities-hero">
        <FadeIn><p className="overline mb-5">Beyond the Cottage</p></FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            Everything that comes
            <br />
            <em className="not-italic text-clay">with your stay.</em>
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            Beyond your cottage, the whole property is yours to explore. These shared spaces
            are open to all overnight guests, at no extra cost. We ask that you let us know
            ahead of time so we can keep things uncrowded and personal — that way no one
            ever feels like they have to share what they came here to find.
          </p>
        </FadeIn>
      </section>

      <section className="container-x pb-24 lg:pb-32" data-testid="amenities-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
          {AMENITIES.map((a, i) => {
            const isLast = i === AMENITIES.length - 1;
            const remainder = AMENITIES.length % 3;
            const centerClass =
              isLast && remainder === 1 ? "lg:col-start-2" :
              isLast && remainder === 2 ? "" : "";
            const mdCenter =
              isLast && AMENITIES.length % 2 === 1 ? "md:col-span-2 md:max-w-md md:mx-auto lg:col-span-1 lg:max-w-none lg:mx-0" : "";
            return (
              <div key={a.title} className={`${centerClass} ${mdCenter}`}>
                <FadeIn delay={i * 60}>
                  <article className="group" data-testid={`amenity-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                    <RotatingImage gallery={a.gallery} alt={a.title} onClick={(idx) => openLightbox(a, idx)} shapeIndex={i} />
                    {a.placeholder && (
                      <p className="mt-2 text-xs italic text-coal/45 tracking-wide">Photos coming soon</p>
                    )}
                    <h3 className="mt-4 font-serif text-2xl text-moss font-light leading-tight tracking-tight">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-coal/75 text-base leading-[1.8]">{a.desc}</p>
                  </article>
                </FadeIn>
              </div>
            );
          })}
        </div>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="amenities-skyline-divider" />

      {/* Closing note */}
      <section className="bg-oat py-20 lg:py-28" data-testid="amenities-note">
        <div className="container-narrow text-center">
          <FadeIn>
            <p className="font-serif italic text-2xl lg:text-3xl text-moss font-light leading-[1.45] tracking-tight max-w-2xl mx-auto">
              "Take what you need. Leave what you can. The land asks for very little, and
              gives back more than it should."
            </p>
          </FadeIn>
          <FadeIn delay={180}>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/stay" className="btn-primary" data-testid="amenities-stay-btn">
                Reserve a cottage
              </Link>
              <Link to="/visit" className="btn-ghost" data-testid="amenities-contact-btn">
                Ask a question
              </Link>            </div>
          </FadeIn>
        </div>
      </section>

      <Lightbox
        open={lb.open}
        images={lb.images}
        index={lb.index}
        title={lb.title}
        onClose={() => setLb({ ...lb, open: false })}
        onIndexChange={(i) => setLb({ ...lb, index: i })}
      />
    </div>
  );
}
